import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

export default function Overhead360Page() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://funtimephotobooths.com';
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useSEO({
    title: 'Overhead 360 Video Booth Rental San Diego | Fun Time Photobooths',
    description: 'Experience the ultimate overhead 360 video booth in San Diego. Capture stunning slow-motion videos from above with instant sharing for weddings, corporate events, and celebrations.',
    keywords: 'overhead 360 booth San Diego, 360 video booth rental, overhead video booth, slow motion booth, wedding 360 booth, corporate 360 video booth',
    canonical: `${siteUrl}/overhead-360`,
    ogTitle: 'Overhead 360 Video Booth Rental San Diego | Fun Time Photobooths',
    ogDescription: 'State-of-the-art overhead 360 video booth with slow-motion effects and instant sharing for unforgettable event experiences.',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          name: 'Overhead 360 Video Booth',
          description: 'State-of-the-art overhead 360 video booth with slow-motion effects and instant sharing for weddings, corporate events, and celebrations in San Diego.',
          provider: {
            '@type': 'LocalBusiness',
            name: 'Fun Time Photobooths',
            telephone: '+14424160576',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'San Diego',
              addressRegion: 'CA',
              addressCountry: 'US',
            },
          },
          areaServed: {
            '@type': 'City',
            name: 'San Diego',
          },
          url: `${siteUrl}/overhead-360`,
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is an overhead 360 video booth?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'An overhead 360 video booth uses a state-of-the-art camera mounted above your guests that rotates to capture stunning 360-degree slow-motion footage. Unlike traditional floor-based 360 booths, our overhead system keeps everyone safely on the ground.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is the overhead 360 booth safer than platform booths?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! Unlike traditional platform 360 booths where guests stand on elevated surfaces, our overhead system keeps everyone safely on the ground—completely eliminating the risk of falls or injuries.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I share the 360 videos on social media?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Absolutely. Videos are instantly available for sharing on social media directly from the booth, keeping the excitement going long after your event.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I add custom branding to my 360 video?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! You can add your logo, event hashtag, or custom overlays to make each video uniquely yours. Our team handles all customization to match your event theme.',
              },
            },
            {
              '@type': 'Question',
              name: 'What events is the overhead 360 video booth perfect for?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Our overhead 360 video booth is perfect for weddings, corporate events, birthday celebrations, anniversaries, and any special occasion where you want to create unforgettable moments.',
              },
            },
          ],
        },
      ],
    },
  });

  const galleryImages = [
    {
      url: 'https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/987034957086cc4729d38db4461758f9.jpeg',
      alt: 'Overhead 360 booth at elegant wedding celebration'
    },
    {
      url: 'https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/43809fa874e81b1d7b07d5a532847a61.jpeg',
      alt: 'Corporate event with 360 video booth'
    },
    {
      url: 'https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/ba29ebafd98f9d99242c0ec2e9819f52.jpeg',
      alt: 'Birthday celebration with overhead 360 booth'
    },
    {
      url: 'https://static.readdy.ai/image/4e6cb48aacd8b48acab6684eff653bcc/1acbb663b6c99339cd5e76b276aed1c9.jpeg',
      alt: 'Overhead 360 booth at special event'
    }
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <iframe
            src="https://www.youtube.com/embed/tGdXxrtT6Yw?autoplay=1&mute=1&loop=1&playlist=tGdXxrtT6Yw&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1"
            className="absolute top-1/2 left-1/2 w-[177.77777778vh] h-[56.25vw] min-h-full min-w-full -translate-x-1/2 -translate-y-1/2"
            allow="autoplay; encrypted-media"
            title="Overhead 360 Video Background"
            style={{ border: 'none', pointerEvents: 'none' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ textShadow: '0 0 20px rgba(249, 115, 22, 0.8), 0 0 40px rgba(249, 115, 22, 0.5), 0 0 60px rgba(249, 115, 22, 0.3)' }}>
            Overhead 360 Video Booth
          </h1>
          <p className="text-xl md:text-2xl text-white">
            The Ultimate Event Experience
          </p>
        </div>
      </section>

      {/* Video Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Watch real videos from our overhead 360 booth and see the magic for yourself
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-lg">
              <div className="relative" style={{ paddingBottom: '177.78%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://drive.google.com/file/d/1Tq_qRP76HmmTaYO7QAfannRG2QNjJIUS/preview"
                  allow="autoplay"
                  allowFullScreen
                  title="Overhead 360 booth wedding celebration video"
                  style={{ border: 'none' }}
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Birthday Celebration</h3>
                <p className="text-gray-600">
                  Capturing the joy and excitement of a birthday celebration
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-lg">
              <div className="relative" style={{ paddingBottom: '177.78%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://drive.google.com/file/d/1bv1hzNQdbyjYvbhyyvTbz8OaGiiqSgXk/preview"
                  allow="autoplay"
                  allowFullScreen
                  title="Overhead 360 booth corporate event video"
                  style={{ border: 'none' }}
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Corporate Event</h3>
                <p className="text-gray-600">Professional team celebrating success with dynamic 360° footage</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Makes Our 360 Booth Special
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our overhead 360 video booth in San Diego delivers slow-motion videos using a professionally rigged overhead system. Unlike traditional floor-based 360 booths, our setup allows more guest, better angels, and a safer experience for weddings, corporate events, and private celebrations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-camera-rotate-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                360° Overhead Camera
              </h3>
              <p className="text-gray-600 mb-4">
                Our state-of-the-art overhead camera system captures stunning footage from every angle as it rotates around your guests.
              </p>
              <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '177.78%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/BUGj2a4I_R0?controls=1&rel=0&modestbranding=1"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="360 overhead camera in action"
                  style={{ border: 'none' }}
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-movie-2-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Slow-Motion Magic
              </h3>
              <p className="text-gray-600 mb-4">
                Create cinematic slow-motion videos that capture every detail and make your guests feel like movie stars.
              </p>
              <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '177.78%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/SQi0cHbfoac?controls=1&rel=0&modestbranding=1"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Slow-motion 360 booth example"
                  style={{ border: 'none' }}
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Safer Than Platform Booths
              </h3>
              <p className="text-gray-600 mb-4">
                Unlike traditional platform 360 booths where guests stand on elevated surfaces, our overhead system keeps everyone safely on the ground—no risk of falls or injuries.
              </p>
              <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '177.78%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/HZGdR5K6D44?controls=1&rel=0&modestbranding=1"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Why overhead 360 is safer than platform booths"
                  style={{ border: 'none' }}
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-share-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Instant Sharing
              </h3>
              <p className="text-gray-600">
                Videos are instantly available for sharing on social media, keeping the excitement going long after your event.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-palette-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Custom Branding
              </h3>
              <p className="text-gray-600">
                Add your logo, event hashtag, or custom overlays to make each video uniquely yours.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-user-star-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Expert Attendants
              </h3>
              <p className="text-gray-600">
                Our professional team manages everything, ensuring smooth operation and helping guests create amazing content.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Past Events Gallery
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See our overhead 360 booth in action at weddings, corporate events, and celebrations across San Diego
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative overflow-hidden rounded-lg cursor-pointer group"
                onClick={() => setSelectedImage(image.url)}
              >
                <div className="w-full h-80">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          <div className="max-w-6xl w-full h-full flex items-center justify-center">
            <img
              src={selectedImage}
              alt="Gallery image"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Perfect For Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Perfect For Every Occasion
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 text-center shadow-lg">
              <i className="ri-heart-3-line text-5xl text-orange-500 mb-4"></i>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Weddings</h3>
              <p className="text-gray-600">
                Give your guests an unforgettable experience and create stunning keepsakes from your special day.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center shadow-lg">
              <i className="ri-briefcase-line text-5xl text-orange-500 mb-4"></i>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Corporate Events</h3>
              <p className="text-gray-600">
                Boost engagement and create shareable content that extends your brand reach on social media.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center shadow-lg">
              <i className="ri-cake-3-line text-5xl text-orange-500 mb-4"></i>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Celebrations</h3>
              <p className="text-gray-600">
                From birthdays to anniversaries, make any celebration extraordinary with our 360 video booth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Book Your 360 Experience?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us today to check availability and get a custom quote for your event.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-500 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Your Quote
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
